import { Divider, FormControl, Stack, TextField, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { ChatMessage } from './chat-message'
import SendIcon from '@mui/icons-material/Send'
import { Controller, useForm } from 'react-hook-form'
import { IThreadTopic } from 'types/threads'
import { useCreateThreadMessageMutation, useThreadMessagesQuery } from 'services/thread'
import { v4 as uuidv4 } from 'uuid'
import { LoadingButton } from '@mui/lab'
import { zodResolver } from '@hookform/resolvers/zod'
import { createMessageSchema } from './validations'
import { Error } from 'components/Error'
import { Loading } from 'components/Loading'
import { toast } from 'react-toastify'
import { useEffect, useState } from 'react'
import { FileUpload } from 'components/FileUpload'
import { OrganizationType } from '../../../types/organization'
import { useUserCurrentQuery } from '../../../services/users'

type FormFields = {
  message: string
  uploadedFileId?: string
}

type Props = {
  selectedTopic: IThreadTopic
}

export const ChatBox = ({ selectedTopic }: Props) => {
  const { t } = useTranslation()
  const schema = createMessageSchema(t)
  const [triggerDeleteFile, setTriggerDeleteFile] = useState(false)

  const { data: currentUser } = useUserCurrentQuery()

  const [createThreadMessage, { isError, isLoading }] =
    useCreateThreadMessageMutation()

  const args = {
    page: 0,
    size: 500,
    topicId: selectedTopic.id,
  } as const

  const {
    data: threadMessages,
    isLoading: isMessagesLoading,
    isError: isMessagesError,
  } = useThreadMessagesQuery(args, { skip: !selectedTopic.id })

  const {
    handleSubmit,
    control,
    reset,
    formState: { isValid },
    setValue,
    watch,
  } = useForm<FormFields>({
    defaultValues: {
      message: '',
      uploadedFileId: '',
    },
    resolver: zodResolver(schema),
  })

  const onSubmit = ({ message, uploadedFileId }: FormFields) => {
    createThreadMessage({
      id: uuidv4(),
      topicId: selectedTopic.id,
      messageBody: message,
      uploadedFileId,
    })
    reset()
    setTriggerDeleteFile(true)
  }

  useEffect(() => {
    if (isError) {
      toast.error(t('failedToCreate'))
    }
  }, [isError, t])

  if (isMessagesLoading) {
    return <Loading fullPage={false} />
  }

  if (isMessagesError) {
    return <Error fullPage={false} />
  }

  if (threadMessages === undefined) {
    return <></>
  }

  const clientOrgName = selectedTopic.receiverOrganizationType === OrganizationType.CLIENT ? selectedTopic.receiverOrganizationName : selectedTopic.senderOrganizationName
  const tenderOrgName = selectedTopic.receiverOrganizationType === OrganizationType.TENDER_AUTHORITY ? selectedTopic.receiverOrganizationName : selectedTopic.senderOrganizationName

  return (
    <Stack flexGrow={1} spacing={1}>
      <Stack>
        <Typography>
          {t('procurementOrganizationName')}:{tenderOrgName}
        </Typography>
        <Typography>
          {t('contractorOrganizationName')}:{clientOrgName}
        </Typography>
      </Stack>
      <Divider flexItem />
      <Stack flexGrow={1} sx={{ overflow: 'auto' }} spacing={1}>
        {/* TODO. Check current user and get organizationId and use it to detect chatMessage alignment */}
        {threadMessages.items.map((message) => (
          <ChatMessage align={message.creatorOrganizationId === currentUser?.organizationId ? 'right' : 'left'}
                       message={message} />
        ))}
      </Stack>
      <Divider flexItem />
      <Stack alignItems="center" direction="row" spacing={1}>
        <Controller
          name="message"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              style={{ width: '100%' }}
              multiline
              minRows={2}
              size="small"
              variant="outlined"
            />
          )}
        />

        <LoadingButton
          disabled={!isValid}
          loading={isLoading}
          sx={{ width: 48, height: 48 }}
          onClick={handleSubmit(onSubmit)}
        >
          <SendIcon sx={{ fontSize: 32 }} />
        </LoadingButton>
      </Stack>
      <FormControl sx={{ minWidth: 120 }} fullWidth margin="dense">
        <Controller
          name="uploadedFileId"
          control={control}
          render={({ field }) => (
            <FileUpload
              {...field}
              text={t('creationOrderFile')}
              handleFile={(file) => {
                setTriggerDeleteFile(false)
                field.onChange(file.id)
              }}
              onDelete={() => {
                setValue('uploadedFileId', '')
                setTriggerDeleteFile(false)
              }}
              triggerDelete={triggerDeleteFile}
            />
          )}
        />
      </FormControl>
    </Stack>
  )
}
